import DataLoader from 'dataloader'
import { supplierPortalPath } from '@omnea-app/fe-api-client/supplierPortalPath'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { UsersHttpClient } from '@omnea-app/users/usersHttpClient'

export type User = {
  /** Format: uuid */
  id: string
  firstName: string
  lastName: string
  email: string
  isArchived: boolean
  /** @enum {string} */
  role: 'user' | 'editor'
  isOutOfOffice?: boolean
}

type GetToken = () => Promise<string>

// Ids requested by different components within this window merge into one POST /by-ids.
const BATCH_WINDOW_MS = 10

// The endpoint schema caps userIds at 5000; stay well below it per request.
const MAX_IDS_PER_REQUEST = 1000

// All callers share one authenticated session; the token is resolved at flush time.
let latestGetToken: GetToken | null = null
let latestIsSupplier = false

const fetchUsers = async (userIds: ReadonlyArray<string>): Promise<Array<User | null>> => {
  const getToken = latestGetToken
  if (getToken == null) {
    // Should never happen - fetchUsersByIdsBatched sets it before every load
    throw new Error('fetchUsers called before a getToken was provided')
  }
  const token = await getToken()
  const apiClient = new UsersHttpClient({ omneaJwt: token })

  const { data, error } = await apiClient.POST(supplierPortalPath('/by-ids', latestIsSupplier), {
    body: { userIds: [...new Set(userIds)] },
  })
  throwIfApiError(error)

  const usersById = new Map(data.map((user) => [user.id, user]))
  // Ids the backend filtered out (deleted users, suppliers) resolve to null.
  return userIds.map((userId) => usersById.get(userId) ?? null)
}

const createLoader = () =>
  new DataLoader<string, User | null>(fetchUsers, {
    maxBatchSize: MAX_IDS_PER_REQUEST,
    batchScheduleFn: (callback) => setTimeout(callback, BATCH_WINDOW_MS),
  })

let loader = createLoader()

export const fetchUsersByIdsBatched = async (userIds: string[], getToken: GetToken, isSupplier: boolean): Promise<User[]> => {
  latestGetToken = getToken
  latestIsSupplier = isSupplier
  const uniqueIds = [...new Set(userIds)]
  // Not loadMany: it resolves errors as values, but a failed batch must reject every caller.
  const users = await Promise.all(
    uniqueIds.map((userId) =>
      loader.load(userId).then((user) => {
        // Memoize ids only while in flight: clear on success; DataLoader clears failed keys itself.
        loader.clear(userId)
        return user
      })
    )
  )
  return users.filter((user): user is User => user != null)
}

export const resetUsersByIdsBatcherForTests = () => {
  loader = createLoader()
  latestGetToken = null
  latestIsSupplier = false
}
